<template>
  <div>
    <div v-if="!this.mode.startsWith('group')"
         class="dp-input mb-3"
         :class="{
        'clearable': this.canClear,
        'can-be-cleared': this.canClear && this.modelValue,
        'form-floating': this.isFloating,
        'has-error': this.errors && this.name && this.errors[this.name],
       }"
    >
      <dp-field
        :label="label"
        :name="name"
        :rules="rules"
        type="number"
        class="form-control"
        :class="fieldClasses"
        :id="name"
        :min="min"
        :max="max"
        :step="step"
        :modelValue="modelValue"
        :placeholder="this.getPlaceholder()"
        :disabled="disabled"
        @update:modelValue="updateValue"
      />

      <label :for="name" class="form-label fw-normal">
        {{ label }}<strong v-show="checkRequired(rules)"> *</strong>
      </label>

      <button v-if="this.canClear && this.modelValue"
              @click.stop.prevent="this.clearValue"
              class="btn-close"
              type="reset"></button>

      <dp-error-message class="invalid-feedback d-block" :name="name"></dp-error-message>
    </div>

    <template v-else-if="mode.startsWith('group')">
      <div class="input-group mb-3">
      <span v-if="this.mode === 'group-start' && this.addonText" class="input-group-text" :id="name + 'Addon'">
        {{ this.addonText }}
      </span>

        <div class="dp-input form-floating"
             :class="{
              'clearable': this.canClear,
              'can-be-cleared': this.canClear && this.modelValue,
              'has-error': this.errors && this.name && this.errors[this.name],
           }"
        >
          <dp-field
            :label="label"
            :name="name"
            :rules="rules"
            type="number"
            class="form-control"
            :class="fieldClasses"
            :id="name"
            :min="min"
            :max="max"
            :step="step"
            :modelValue="modelValue"
            :placeholder="placeholder"
            :aria-describedby="name + 'Addon'"
            :disabled="disabled"
            @update:modelValue="updateValue"
          />

          <label v-if="label" :for="name" class="form-label">
            {{ label }}<strong v-show="checkRequired(rules)"> *</strong>
          </label>

          <button v-if="this.canClear && this.modelValue"
                  @click.stop.prevent="this.clearValue"
                  class="btn-close"></button>
        </div>

        <!-- Lets a caller replace the read-only addon with an interactive control
             (e.g. a unit dropdown) without leaving the input group. -->
        <slot name="afterInput">
        <span v-if="this.mode === 'group-end' && this.addonText" class="input-group-text" :id="name + 'Addon'">
          {{ this.addonText }}
        </span>
      </slot>

        <!-- Full width, so it wraps onto its own line underneath the group rather than becoming a
             third column next to the input and its addon. Without this the field only turned red
             and never said what was wrong with it. -->
        <dp-error-message class="invalid-feedback d-block w-100" :name="name"></dp-error-message>
      </div>
    </template>
  </div>
</template>

<script>
import {InputSizeEnum} from "@/models/enum/InputSizeEnum";

export default {
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Number,
    },
    label: {
      type: String,
    },
    name: {
      type: String,
    },
    errors: {
      type: Object,
    },
    rules: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    min: {
      type: Number,
      required: false,
    },
    max: {
      type: Number,
      required: false,
    },
    step: {
      type: Number,
      required: false,
      default: 1,
    },
    mode: {
      type: String,
      required: false,
      default: 'float',
      validate(value) {
        return value === 'float' || value === 'group-start' || value === 'group-end';
      }
    },
    addonText: {
      type: String,
      required: false,
      validate(value) {
        return this.mode.startsWith('group') && value;
      }
    },
    inputSize: {
      type: String,
      required: false,
      default: InputSizeEnum.SM,
    },
    fieldClass: {
      type: String,
      required: false,
      default: '',
    },

    // BOOLEANS
    canClear: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    isFloating: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      enums: {
        inputSizeEnum: InputSizeEnum,
      },
    }
  },
  computed: {
    fieldClasses() {
      return {
        'form-control-sm': (this.inputSize === this.enums.inputSizeEnum.SM),
        'form-control-lg': (this.inputSize === this.enums.inputSizeEnum.LG),
        'is-invalid': this.errors && this.name && this.errors[this.name],
        [this.fieldClass]: this.fieldClass
      };
    }
  },
  methods: {
    checkRequired(rules) {
      if (rules && rules.includes("required")) {
        return true;
      }
    },
    determinePlaceholder() {
      return null;
    },
    getPlaceholder() {
      return this.determinePlaceholder() ?? this.placeholder ?? this.label;
    },
    clearValue() {
      this.$emit('update:modelValue', null);
    },
    updateValue(value) {
      value = parseFloat(value);
      if (this.min && value < this.min) {
        value = this.min;
      } else if (this.max && value > this.max) {
        value = this.max;
      }

      this.$emit('update:modelValue', value);
    },
  },
};
</script>

<style scoped lang="scss">
.form-floating label {
  display: block;
  margin-bottom: 0.5rem;
}

input:disabled {
  cursor: not-allowed;
}

.dp-input {
  &.clearable {
    position: relative;

    button.btn-close {
      position: absolute;
      bottom: 0;
      right: 0;
      padding: 0.7rem 0.5rem;
    }
  }

  &.can-be-cleared input {
    padding-right: 2rem;
  }
}
</style>
